import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Switch, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { usePWG304Query } from './hooks/usePWG304Query';
import type { ResultRow, WriteoffRow } from '../../services/pwg304Api';
import './PWG304Form.css';

const { RangePicker } = DatePicker;

const PWG304Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWG304Query();

  const [companyFrom, setCompanyFrom] = useState('');
  const [companyTo, setCompanyTo] = useState('');
  const [docFrom, setDocFrom] = useState('');
  const [docTo, setDocTo] = useState('');
  const [payDateRange, setPayDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [excludeZeroBalance, setExcludeZeroBalance] = useState(false);

  const handleQuery = () => {
    runQuery({
      companyFrom: companyFrom.trim() || undefined,
      companyTo: companyTo.trim() || undefined,
      docFrom: docFrom.trim() || undefined,
      docTo: docTo.trim() || undefined,
      payDateFrom: payDateRange?.[0]?.format('YYYY-MM-DD') || undefined,
      payDateTo: payDateRange?.[1]?.format('YYYY-MM-DD') || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      salesFrom: salesFrom.trim() || undefined,
      salesTo: salesTo.trim() || undefined,
      excludeZeroBalance,
    });
  };

  const handleReset = () => {
    setCompanyFrom(''); setCompanyTo('');
    setDocFrom(''); setDocTo('');
    setPayDateRange(null);
    setDeptFrom(''); setDeptTo('');
    setSalesFrom(''); setSalesTo('');
    setExcludeZeroBalance(false);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const rows = data.map(r => {
      const writeoffRows = (r.writeoffs || []).map(w =>
        `<tr style="background:#fafafa"><td></td><td colspan="2" style="padding-left:20px;font-size:11px">核銷單號: ${w.FG06_02} → ${w.FG06_03} (${w.FG06_141})</td><td>${w.FG06_12}</td><td class="right">${w.FG06_13.toFixed(2)}</td><td class="right">${w.FG06_15.toFixed(2)}</td><td></td></tr>`
      ).join('');
      return `<tr>
        <td>${r.FG05_01}</td>
        <td>${r.FG05_02}</td>
        <td>${r.payDate?.substring(0, 10) || ''}</td>
        <td>${r.FG05_11}</td>
        <td class="right">${r.FG05_12.toFixed(2)}</td>
        <td class="right">${r.balance.toFixed(2)}</td>
        <td class="right">${r.writeoffs?.length || 0}</td>
      </tr>${writeoffRows}`;
    }).join('');
    win.document.write(`<!DOCTYPE html><html><head><title>預收帳款明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>預收帳款明細表</h2>
<table><thead><tr><th>公司</th><th>單號</th><th>繳款日期</th><th>幣別</th><th>預收金額</th><th>餘額</th><th>核銷筆數</th></tr></thead><tbody>
${rows}
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const expandedRowRender = (record: ResultRow) => {
    if (!record.writeoffs || record.writeoffs.length === 0) {
      return <span style={{ color: '#999', fontSize: 12 }}>無核銷明細</span>;
    }
    const woCols: ColumnsType<WriteoffRow> = [
      { title: '核銷單號', dataIndex: 'FG06_02', key: 'FG06_02', width: 140 },
      { title: '對應單號', dataIndex: 'FG06_03', key: 'FG06_03', width: 140 },
      { title: '類型', dataIndex: 'FG06_141', key: 'FG06_141', width: 80 },
      { title: '幣別', dataIndex: 'FG06_12', key: 'FG06_12', width: 70 },
      { title: '核銷金額', dataIndex: 'FG06_13', key: 'FG06_13', width: 110, align: 'right' as const,
        render: (v: number) => v.toFixed(2) },
      { title: '核銷後餘額', dataIndex: 'FG06_15', key: 'FG06_15', width: 120, align: 'right' as const,
        render: (v: number) => v.toFixed(2) },
    ];
    return (
      <Table<WriteoffRow>
        columns={woCols}
        dataSource={record.writeoffs}
        rowKey={(r) => `${r.FG06_02}-${r.FG06_03}`}
        size="small"
        pagination={false}
        className="pwg304-writeoffs"
      />
    );
  };

  const columns: ColumnsType<ResultRow> = [
    { title: '公司', dataIndex: 'FG05_01', key: 'FG05_01', width: 80 },
    { title: '單號', dataIndex: 'FG05_02', key: 'FG05_02', width: 140 },
    { title: '繳款日期', dataIndex: 'payDate', key: 'payDate', width: 110,
      render: (v: string) => v?.substring(0, 10) || '' },
    { title: '幣別', dataIndex: 'FG05_11', key: 'FG05_11', width: 70 },
    { title: '預收金額', dataIndex: 'FG05_12', key: 'FG05_12', width: 120, align: 'right' as const,
      render: (v: number) => v.toFixed(2) },
    { title: '沖銷金額', dataIndex: 'FG05_13', key: 'FG05_13', width: 120, align: 'right' as const,
      render: (v: number) => v.toFixed(2) },
    { title: '調整金額', dataIndex: 'FG05_41', key: 'FG05_41', width: 120, align: 'right' as const,
      render: (v: number) => v.toFixed(2) },
    { title: '餘額', dataIndex: 'balance', key: 'balance', width: 120, align: 'right' as const,
      render: (v: number) => (
        <span style={{ color: v < 0 ? '#cf1322' : undefined }}>{v.toFixed(2)}</span>
      ) },
    { title: '核銷筆數', key: 'writeoffCount', width: 90, align: 'right' as const,
      render: (_: unknown, r: ResultRow) => r.writeoffs?.length || 0 },
  ];

  return (
    <PermissionGuard module="pwg304f1" action="query">
      <div className="pwg304-container">
        <div className="pwg304-header">
          <h2>預收帳款明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>公司起</label>
              <Input value={companyFrom} onChange={e => setCompanyFrom(e.target.value)}
                placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>公司迄</label>
              <Input value={companyTo} onChange={e => setCompanyTo(e.target.value)}
                placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>單號起</label>
              <Input value={docFrom} onChange={e => setDocFrom(e.target.value)}
                placeholder="起始單號" style={{ width: 140 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>單號迄</label>
              <Input value={docTo} onChange={e => setDocTo(e.target.value)}
                placeholder="結束單號" style={{ width: 140 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>繳款日期</label>
              <RangePicker
                value={payDateRange}
                onChange={val => setPayDateRange(val as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
                style={{ width: 240 }}
                allowClear
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門起</label>
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)}
                placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門迄</label>
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)}
                placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>業務起</label>
              <Input value={salesFrom} onChange={e => setSalesFrom(e.target.value)}
                placeholder="起" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>業務迄</label>
              <Input value={salesTo} onChange={e => setSalesTo(e.target.value)}
                placeholder="迄" style={{ width: 100 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>排除零餘額</label>
              <Switch checked={excludeZeroBalance} onChange={setExcludeZeroBalance} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<ResultRow>
            columns={columns}
            dataSource={data}
            rowKey="FG05_02"
            loading={loading}
            size="small"
            expandable={{ expandedRowRender, defaultExpandAllRows: false }}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1000 }}
            summary={pageData => {
              const totalAdvance = pageData.reduce((s, r) => s + (r.FG05_12 || 0), 0);
              const totalBalance = pageData.reduce((s, r) => s + (r.balance || 0), 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <strong>本頁合計</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{totalAdvance.toFixed(2)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} />
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align="right">
                    <strong style={{ color: totalBalance < 0 ? '#cf1322' : undefined }}>
                      {totalBalance.toFixed(2)}
                    </strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} />
                </Table.Summary.Row>
              );
            }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWG304Form;
